'use client';
import * as React from 'react';
import {
  Box,
  InputAdornment,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { InputProps, TextInput } from '@coverright/ui/inputs/text-input';
import { SelectInput } from '@coverright/ui/inputs/select-input';
import { useEffect, useState } from 'react';
import { useCountiesByZipLazyQuery } from '@coverright/data-access/medicare/documents';
import { ZipCountyFips } from '@coverright/data-access/types/medicare';

type Location = {
  zip: string;
  county: string;
  state: string;
};

interface Props {
  zip?: string;
  county?: string;
  state?: string;
  onChange: (location: Location) => void;
  onLoadingChange?: (value?: boolean) => void;
  inputProps?: InputProps;
}

export function ZipSelectorCompact({ onChange, onLoadingChange, zip = '', state = '', county = '', inputProps }: Props) {
  const theme = useTheme();
  const [location, setLocation] = useState<Location>({
    zip,
    county,
    state,
  });
  const [counties, setCounties] = useState<ZipCountyFips[]>([]);
  const inpRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const [getCounties, { loading, called }] = useCountiesByZipLazyQuery({
    onCompleted: (data) => {
      const result = data.plans_zip_county_fips;
      if (result.length === 1) {
        setLocation((prev) => {
          const newValue = {
            ...prev,
            county: result[0].countyname,
            state: result[0].state,
          }
          onChange(newValue);
          return newValue
        });
      } else if (result.length > 1) {
        setOpen(true);
      }
      setCounties(result);
    },
  });

  useEffect(() => {
    onLoadingChange && onLoadingChange(loading)
  }, [loading, onLoadingChange]);

  // The zip can arrive after mount -- a form prefilling it from a partner roster, or a client
  // record finishing loading. The state above is seeded once, so without this whatever was there at
  // mount is all this ever shows and setting the zip from outside does nothing at all.
  //
  // Keyed on the prop alone, so it fires only when the parent changes it: typing updates `location`
  // but not the prop, and a parent that merely echoes our own onChange back sees the values already
  // equal and does nothing.
  useEffect(() => {
    if (zip === location.zip) return;
    setLocation({ zip, county, state });
    setCounties([]);
    if (isValid(zip)) {
      getCounties({ variables: { zip } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);

  const handleInputChange = async (value: string) => {
    setLocation({ zip: value, county: '', state: '' });
    setCounties([]);
    if (isValid(value)) {
      await getCounties({ variables: { zip: value } });
    } else {
      onChange({ zip: '', county: '', state: '' });
    }
  };

  const handlePopoverChange = (value: string) => {
    const county = counties.find((c) => c.countyname === value);
    if (county) {
      setLocation((prev) => {
        const newValue = {
          ...prev,
          county: county.countyname,
          state: county.state,
        }
        onChange(newValue);
        return newValue
      });
    }
    setOpen(false);
  };

  const handlePopoverClose = () => {
    if (!location.county) {
      setLocation({ zip: '', county: '', state: '' });
      onChange({ zip: '', county: '', state: '' });
    }
    setOpen(false);
  };

  const error =
    /^[0-9]{5}$/.test(location.zip) && !loading && called && !counties.length && !location.county
      ? "We can't find your county"
      : undefined;

  const endAdornment = location.county ? (
    <InputAdornment
      sx={{
        '& p': { maxWidth: 125, overflow: 'hidden', textOverflow: 'ellipsis' },
      }}
      position="end"
    >
      <Typography variant={'body2'} sx={{ color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#475467' }}>
        {location.county}{location.state && `, ${location.state}`}
      </Typography>
    </InputAdornment>
  ) : undefined;

  return (
    <>
      <TextInput
        placeholder={'Enter your zip code'}
        ref={inpRef}
        hideTick
        value={location.zip}
        sx={{ mb: 0 }}
        loading={loading}
        onChange={(e) => handleInputChange(e.target.value)}
        {...inputProps}
        inputProps={{
          ...inputProps?.inputProps,
          maxLength: 5,
        }}
        InputProps={{
          ...inputProps?.InputProps,
          endAdornment,
          onFocus: e => {
            e.target.select();
          }
        }}
        type={'tel'}
        error={inputProps?.error || !!error}
        helperText={inputProps?.helperText || error}
      />
      <CountiesPopover
        counties={counties}
        onClose={handlePopoverClose}
        value={location.county}
        anchorEl={inpRef.current}
        onChange={handlePopoverChange}
        open={open}
      />
    </>
  );
}

interface PopoverProps {
  anchorEl: HTMLDivElement | null;
  onClose: () => void;
  counties: ZipCountyFips[];
  open: boolean;
  value: string;
  onChange: (value: string) => void;
}

const CountiesPopover = ({
  onClose,
  counties,
  anchorEl,
  open,
  onChange,
  value,
}: PopoverProps) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    sx={{zIndex: 1501}}
  >
    <Box p={1} width={'200px'}>
      <SelectInput
        fullWidth
        hideTick
        wrapperSx={{ '& .MuiTextField-root': { mb: 0 } }}
        sx={{
          '& div.MuiOutlinedInput-input': {
            m: '5px 2px 1px!important',
            p: '8px 15px 6px!important',
          },
        }}
        MenuProps={{sx: {zIndex: 1502} }}
        label={'Select county'}
        value={value}
        placeholder={'Select county'}
        onChange={(event) => {
          onChange(event.target.value as string);
        }}
      >
        {counties.map((county) => (
          <MenuItem key={county.countyname} value={county.countyname}>
            {county.countyname}
          </MenuItem>
        ))}
      </SelectInput>
    </Box>
  </Popover>
);

const isValid = (value: any) => /^[0-9]{5}$/.test(String(value));
